export type PivotMethod = 'standard' | 'fibonacci' | 'camarilla' | 'woodie' | 'demark';
export type Timeframe = 'daily' | 'weekly' | 'monthly';
export type SymbolKey = 'eurusd' | 'gbpusd' | 'usdjpy' | 'audusd' | 'usdcad' | 'xauusd';
export type PivotInputs = { high: number; low: number; close: number; open: number };

export const PIVOT_METHODS: { value: PivotMethod; label: string }[] = [
  { value: 'standard', label: 'Standard' },
  { value: 'fibonacci', label: 'Fibonacci' },
  { value: 'camarilla', label: 'Camarilla' },
  { value: 'woodie', label: 'Woodie' },
  { value: 'demark', label: 'DeMark' },
];

export const TIMEFRAMES: { value: Timeframe; label: string }[] = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

export const SYMBOLS: { value: SymbolKey; label: string; pip: number; decimals: number }[] = [
  { value: 'eurusd', label: 'EUR/USD', pip: 0.0001, decimals: 5 },
  { value: 'gbpusd', label: 'GBP/USD', pip: 0.0001, decimals: 5 },
  { value: 'usdjpy', label: 'USD/JPY', pip: 0.01, decimals: 3 },
  { value: 'audusd', label: 'AUD/USD', pip: 0.0001, decimals: 5 },
  { value: 'usdcad', label: 'USD/CAD', pip: 0.0001, decimals: 5 },
  { value: 'xauusd', label: 'XAU/USD', pip: 0.1, decimals: 2 },
];

export const SYMBOL_DEFAULTS: Record<SymbolKey, Record<Timeframe, PivotInputs>> = {
  eurusd: {
    daily: { high: 1.0915, low: 1.0862, close: 1.0893, open: 1.0876 },
    weekly: { high: 1.0972, low: 1.0821, close: 1.0893, open: 1.084 },
    monthly: { high: 1.1038, low: 1.071, close: 1.0893, open: 1.0755 },
  },
  gbpusd: {
    daily: { high: 1.2715, low: 1.2642, close: 1.268, open: 1.2661 },
    weekly: { high: 1.2785, low: 1.258, close: 1.268, open: 1.261 },
    monthly: { high: 1.289, low: 1.245, close: 1.268, open: 1.252 },
  },
  usdjpy: {
    daily: { high: 150.42, low: 149.18, close: 149.85, open: 149.6 },
    weekly: { high: 151.3, low: 148.5, close: 149.85, open: 149 },
    monthly: { high: 153.8, low: 146.2, close: 149.85, open: 147.5 },
  },
  audusd: {
    daily: { high: 0.6612, low: 0.6568, close: 0.659, open: 0.658 },
    weekly: { high: 0.665, low: 0.652, close: 0.659, open: 0.655 },
    monthly: { high: 0.671, low: 0.644, close: 0.659, open: 0.648 },
  },
  usdcad: {
    daily: { high: 1.361, low: 1.3562, close: 1.3588, open: 1.3575 },
    weekly: { high: 1.365, low: 1.351, close: 1.3588, open: 1.353 },
    monthly: { high: 1.372, low: 1.344, close: 1.3588, open: 1.348 },
  },
  xauusd: {
    daily: { high: 1812.5, low: 1798.2, close: 1805.3, open: 1801.8 },
    weekly: { high: 1825, low: 1785, close: 1805.3, open: 1790 },
    monthly: { high: 1840, low: 1768, close: 1805.3, open: 1775 },
  },
};

export const FIB_RETRACEMENT = [0.236, 0.382, 0.5, 0.618, 0.786];
export const FIB_EXTENSION = [1.272, 1.618, 2.618];

export const PIVOT_USE_CASES = [
  {
    icon: 'ri-compass-3-line',
    title: 'Xác định vùng giá tham chiếu',
    desc: 'Xem các mức hỗ trợ và kháng cự được tính từ dữ liệu giá trước đó.',
  },
  {
    icon: 'ri-pulse-line',
    title: 'Theo dõi biến động trong phiên',
    desc: 'Quan sát vị trí hiện tại của giá so với các mức Pivot.',
  },
  {
    icon: 'ri-stack-line',
    title: 'So sánh nhiều khung thời gian',
    desc: 'Kiểm tra các mức Pivot Daily, Weekly và Monthly.',
  },
];

export const FIB_USE_CASES = [
  {
    icon: 'ri-arrow-go-back-line',
    title: 'Phân tích mức thoái lui',
    desc: 'Xác định các mức giá tương ứng với các tỷ lệ Fibonacci trong một biên độ giá.',
  },
  {
    icon: 'ri-focus-3-line',
    title: 'Đánh giá vùng giá',
    desc: 'Xem giá hiện tại nằm ở đâu trong cấu trúc Fibonacci.',
  },
  {
    icon: 'ri-arrow-up-double-line',
    title: 'Phân tích mở rộng',
    desc: 'Sử dụng Fibonacci Extension để tính các mức mở rộng từ vùng giá tham chiếu.',
  },
];

export const STRATEGY_USE_CASES = [
  {
    icon: 'ri-file-chart-line',
    title: 'Kiểm tra dữ liệu giao dịch',
    desc: 'Tổng hợp nhiều giao dịch thành một báo cáo dễ đọc.',
  },
  {
    icon: 'ri-scales-3-line',
    title: 'Đánh giá tính nhất quán',
    desc: 'Xem kết quả có phụ thuộc vào một vài giao dịch riêng lẻ hay không.',
  },
  {
    icon: 'ri-time-line',
    title: 'So sánh giai đoạn',
    desc: 'Kiểm tra hiệu suất của chiến lược theo thời gian, phiên giao dịch hoặc hướng Buy/Sell.',
  },
];

export const WORKFLOW_STEPS = [
  { num: '01', title: 'Xác định dữ liệu giá', desc: 'Thu thập High, Low, Close của khung thời gian cần phân tích.' },
  { num: '02', title: 'Pivot Point', desc: 'Tính các mức hỗ trợ và kháng cự tham chiếu.' },
  { num: '03', title: 'Fibonacci', desc: 'Xác định các vùng giá theo tỷ lệ thoái lui hoặc mở rộng.' },
  { num: '04', title: 'Kiểm tra vùng giá trùng nhau', desc: 'Đối chiếu mức Pivot và Fibonacci gần nhau.' },
  { num: '05', title: 'Phân tích dữ liệu chiến lược', desc: 'Đánh giá hiệu suất bằng dữ liệu giao dịch lịch sử.' },
  { num: '06', title: 'Đánh giá kết quả', desc: 'Rút ra nhận định khách quan từ toàn bộ phân tích.' },
];

export const TOOL_CHOOSER = [
  {
    icon: 'ri-crosshair-2-line',
    name: 'Máy tính Pivot Point',
    scenario: 'Bạn cần các mức giá tham chiếu cho phiên giao dịch',
    desc: 'Tính mức hỗ trợ và kháng cự từ dữ liệu High, Low, Close để xác định những vùng giá quan trọng trước khi vào lệnh.',
    benefits: [
      'Mức hỗ trợ & kháng cự S1–S3, R1–R3',
      '5 phương pháp: Standard, Fibonacci, Camarilla, Woodie, DeMark',
      'Đối chiếu đồng thời Daily, Weekly, Monthly',
    ],
    cta: 'Bắt đầu phân tích',
    anchor: 'pivot',
    accent: 'emerald',
  },
  {
    icon: 'ri-route-line',
    name: 'Máy tính Fibonacci',
    scenario: 'Bạn muốn phân tích vùng hồi và mở rộng của giá',
    desc: 'Tính các mức Fibonacci từ Swing High và Swing Low để nhận diện vùng giá tiềm năng và điểm vào lệnh hợp lý.',
    benefits: [
      'Mức thoái lui 23.6% – 78.6%',
      'Mức mở rộng 127.2% – 261.8%',
      'Kết hợp Pivot để tìm vùng giá confluence',
    ],
    cta: 'Bắt đầu phân tích',
    anchor: 'fibonacci',
    accent: 'amber',
  },
  {
    icon: 'ri-database-2-line',
    name: 'Phân tích dữ liệu chiến lược',
    scenario: 'Bạn có dữ liệu giao dịch cần đánh giá hiệu quả',
    desc: 'Phân tích dữ liệu giao dịch lịch sử để đánh giá hiệu suất và tính nhất quán của một chiến lược.',
    benefits: [
      'Win Rate & Profit Factor',
      'Max Drawdown & Avg Win/Loss',
      'So sánh theo phiên và hướng Buy/Sell',
    ],
    cta: 'Bắt đầu phân tích',
    anchor: 'strategy',
    accent: 'rose',
  },
];

export const GLOSSARY: Record<string, string> = {
  'Win Rate': 'Tỷ lệ phần trăm số lệnh thắng trên tổng số lệnh giao dịch.',
  'Profit Factor': 'Tỷ lệ giữa tổng lợi nhuận và tổng thua lỗ. Lớn hơn 1 nghĩa là lợi nhuận vượt thua lỗ.',
  'Max Drawdown': 'Mức sụt giảm lớn nhất của vốn tính từ đỉnh cao nhất trước đó.',
  'Avg Win/Loss': 'Tỷ lệ giữa lệnh thắng trung bình và lệnh thua trung bình.',
  confluence: 'Vùng giá nơi nhiều mức hỗ trợ / kháng cự trùng nhau, thường được quan sát kỹ hơn.',
};

export const FORMULAS = [
  {
    title: 'Pivot Point (Standard)',
    icon: 'ri-crosshair-2-line',
    formula: 'P = (H + L + C) / 3 · R1 = 2P − L · S1 = 2P − H',
    desc: 'Các mức kháng cự và hỗ trợ được suy ra từ điểm Pivot trung tâm, dùng làm vùng giá tham chiếu cho phiên giao dịch.',
    symbols: [
      { sym: 'P', meaning: 'Điểm Pivot trung tâm' },
      { sym: 'H', meaning: 'Giá cao nhất (High)' },
      { sym: 'L', meaning: 'Giá thấp nhất (Low)' },
      { sym: 'C', meaning: 'Giá đóng cửa (Close)' },
      { sym: 'R1', meaning: 'Mức kháng cự 1' },
      { sym: 'S1', meaning: 'Mức hỗ trợ 1' },
    ],
  },
  {
    title: 'Fibonacci Retracement',
    icon: 'ri-route-line',
    formula: 'Mức = High − (High − Low) × tỷ lệ',
    desc: 'Với xu hướng tăng, các mức thoái lui được tính từ Swing High trừ đi biên độ giá nhân với tỷ lệ Fibonacci (23.6%, 38.2%, 50%, 61.8%, 78.6%).',
    symbols: [
      { sym: 'High', meaning: 'Đỉnh giá (Swing High)' },
      { sym: 'Low', meaning: 'Đáy giá (Swing Low)' },
      { sym: 'tỷ lệ', meaning: 'Tỷ lệ thoái lui Fibonacci' },
    ],
  },
  {
    title: 'Fibonacci Extension',
    icon: 'ri-arrow-up-double-line',
    formula: 'Mức = Low + (High − Low) × tỷ lệ',
    desc: 'Các mức mở rộng (127.2%, 161.8%, 261.8%) chiếu mục tiêu giá ngoài biên độ ban đầu dựa trên xu hướng.',
    symbols: [
      { sym: 'Low', meaning: 'Đáy giá (Swing Low)' },
      { sym: 'High', meaning: 'Đỉnh giá (Swing High)' },
      { sym: 'tỷ lệ', meaning: 'Tỷ lệ mở rộng Fibonacci' },
    ],
  },
  {
    title: 'Win Rate',
    icon: 'ri-percent-line',
    formula: 'Tỷ lệ thắng = Số lệnh thắng / Tổng số lệnh',
    desc: 'Tỷ lệ phần trăm số giao dịch có lợi nhuận dương trên tổng số giao dịch.',
    symbols: [
      { sym: 'Số lệnh thắng', meaning: 'Giao dịch có lợi nhuận' },
      { sym: 'Tổng số lệnh', meaning: 'Toàn bộ giao dịch' },
    ],
  },
  {
    title: 'Profit Factor',
    icon: 'ri-scales-3-line',
    formula: 'PF = Tổng lợi nhuận / Tổng thua lỗ',
    desc: 'Tỷ lệ giữa tổng lợi nhuận và tổng thua lỗ. Giá trị lớn hơn 1 nghĩa là lợi nhuận vượt thua lỗ.',
    symbols: [
      { sym: 'PF', meaning: 'Profit Factor' },
      { sym: 'Tổng lợi nhuận', meaning: 'Tổng số tiền thắng' },
      { sym: 'Tổng thua lỗ', meaning: 'Tổng số tiền thua' },
    ],
  },
  {
    title: 'Max Drawdown',
    icon: 'ri-arrow-down-line',
    formula: 'MDD = (Đỉnh vốn − Đáy vốn) / Đỉnh vốn',
    desc: 'Mức sụt giảm lớn nhất của vốn từ đỉnh cao nhất trước đó, thể hiện rủi ro lịch sử của chiến lược.',
    symbols: [
      { sym: 'MDD', meaning: 'Max Drawdown' },
      { sym: 'Đỉnh vốn', meaning: 'Mức vốn cao nhất' },
      { sym: 'Đáy vốn', meaning: 'Mức vốn thấp nhất' },
    ],
  },
  {
    title: 'Average Win / Average Loss',
    icon: 'ri-funds-line',
    formula: 'Tỷ lệ = Lợi nhuận trung bình / Thua lỗ trung bình',
    desc: 'So sánh quy mô lệnh thắng trung bình với lệnh thua trung bình. Giá trị lớn hơn 1 nghĩa là lệnh thắng lớn hơn lệnh thua.',
    symbols: [
      { sym: 'Lợi nhuận trung bình', meaning: 'Mức lời bình quân mỗi lệnh' },
      { sym: 'Thua lỗ trung bình', meaning: 'Mức lỗ bình quân mỗi lệnh' },
    ],
  },
];

export const NOTES = [
  {
    q: 'Pivot Point được tính như thế nào?',
    a: 'Với phương pháp Standard, điểm Pivot (P) là mức giá trung tâm được tính từ giá cao nhất (High), thấp nhất (Low) và đóng cửa (Close) của chu kỳ trước đó: P = (H + L + C) / 3. Từ P, các mức kháng cự và hỗ trợ được suy ra như R1 = 2P − L và S1 = 2P − H.',
  },
  {
    q: 'Các phương pháp Pivot khác nhau như thế nào?',
    a: 'Standard dùng trung bình cộng của High, Low và Close. Fibonacci đưa các tỷ lệ Fibonacci vào biên độ giá để xác định các mức hỗ trợ / kháng cự. Camarilla tạo ra nhiều cấp gần giá hơn (S1–S4, R1–R4), hợp với giao dịch trong ngày. Woodie nhấn mạnh giá đóng cửa với trọng số gấp đôi: P = (H + L + 2C) / 4. DeMark dựa vào mối quan hệ giữa giá mở cửa (Open) và đóng cửa (Close) để tính điểm Pivot.',
  },
  {
    q: 'Nên dùng khung thời gian Daily, Weekly hay Monthly?',
    a: 'Daily phù hợp cho giao dịch trong ngày, cho các mức giá tham chiếu của phiên hiện tại. Weekly dùng cho giao dịch swing kéo dài vài ngày đến vài tuần. Monthly dùng cho nhà giao dịch dài hạn hoặc đầu tư, vì các mức này ít thay đổi và phản ánh xu hướng lớn. Nhiều nhà giao dịch kết hợp cả ba khung thời gian để đối chiếu các vùng giá trùng nhau.',
  },
  {
    q: 'Fibonacci Retracement sử dụng dữ liệu nào?',
    a: 'Fibonacci Retracement được tính từ hai điểm giá: Swing High và Swing Low. Các mức thoái lui là các tỷ lệ phần trăm (23.6%, 38.2%, 50%, 61.8%, 78.6%) của biên độ giá giữa hai điểm đó.',
  },
  {
    q: 'Fibonacci Retracement và Extension khác nhau thế nào?',
    a: 'Retracement dùng để tìm các mức giá mà xu hướng có thể quay lại (hồi về) trong biên độ hiện tại. Extension dùng để chiếu mục tiêu giá vượt ra ngoài biên độ ban đầu khi xu hướng tiếp tục, với các tỷ lệ 127.2%, 161.8% và 261.8%.',
  },
  {
    q: 'Pivot và Fibonacci có thể cho cùng một mức giá không?',
    a: 'Có thể. Khi một mức Pivot và một mức Fibonacci nằm rất gần nhau, vùng giá đó được gọi là vùng giá trùng lặp (confluence), thường được các nhà giao dịch quan sát kỹ hơn.',
  },
  {
    q: 'Win Rate cho biết điều gì?',
    a: 'Win Rate là tỷ lệ phần trăm số lệnh thắng trên tổng số lệnh giao dịch. Tuy nhiên Win Rate cao chưa chắc đã có lợi nhuận — cần xem cùng với tỷ lệ lời/lỗ trung bình. Một chiến lược thắng ít lệnh nhưng lệnh thắng lớn hơn nhiều so với lệnh thua vẫn có thể mang lại lợi nhuận.',
  },
  {
    q: 'Profit Factor cho biết điều gì?',
    a: 'Profit Factor là tỷ lệ giữa tổng lợi nhuận và tổng thua lỗ. Giá trị trên 1 nghĩa là lợi nhuận nhiều hơn thua lỗ; càng cao thì lợi nhuận càng vượt trội so với thua lỗ.',
  },
  {
    q: 'Max Drawdown được tính như thế nào?',
    a: 'Max Drawdown đo mức sụt giảm lớn nhất của vốn từ đỉnh cao nhất trước đó xuống đáy thấp nhất sau đó, tính theo phần trăm: MDD = (Đỉnh vốn − Đáy vốn) / Đỉnh vốn. Nó phản ánh mức rủi ro lịch sử lớn nhất của chiến lược.',
  },
  {
    q: 'Average Win / Average Loss có ý nghĩa gì?',
    a: 'Đây là tỷ lệ giữa lợi nhuận trung bình của các lệnh thắng và thua lỗ trung bình của các lệnh thua. Giá trị lớn hơn 1 nghĩa là lệnh thắng trung bình lớn hơn lệnh thua, giúp đánh giá chất lượng giao dịch chứ không chỉ số lượng lệnh thắng.',
  },
];

export const TECHNICAL_RELATED_TOOLS = [
  { step: 1, icon: 'ri-crosshair-line', title: 'Máy tính Khối lượng', flow: 'Bắt đầu bằng việc xác định số Lot cần vào lệnh', desc: 'Xác định số Lot dựa trên số dư, rủi ro và Stop Loss.', to: '/may-tinh-khoi-luong', accent: '#059669' },
  { step: 2, icon: 'ri-shield-line', title: 'Máy tính Rủi ro', flow: 'Sau đó kiểm soát số tiền rủi ro cho mỗi lệnh', desc: 'Tính số tiền có thể chịu rủi ro cho mỗi giao dịch.', to: '/quan-tri-rui-ro', accent: '#f59e0b' },
  { step: 3, icon: 'ri-bank-line', title: 'Máy tính Ký quỹ', flow: 'Tiếp theo tính số ký quỹ cần để mở vị thế', desc: 'Tính số tiền ký quỹ cần thiết cho một vị thế.', to: '/may-tinh-ky-quy', accent: '#7c3aed' },
  { step: 4, icon: 'ri-line-chart-line', title: 'Tính toán Lợi nhuận', flow: 'Cuối cùng ước tính lời lỗ ở mức giá mục tiêu', desc: 'Ước tính lời lỗ của một giao dịch ở các mức giá.', to: '/tinh-toan-loi-nhuan', accent: '#10b981' },
];

export const TECHNICAL_BROKERS = [
  {
    name: 'IC Markets',
    initial: 'IC',
    platform: 'MetaTrader 4 / 5 · cTrader',
    spread: 'từ 0.0 pip',
    commission: '$7 / lot',
    leverage: '1:500',
    regulation: 'ASIC · CySEC · SCB',
    color: '#7c3aed',
  },
  {
    name: 'FxPro',
    initial: 'FP',
    platform: 'MetaTrader 4 / 5',
    spread: 'từ 0.0 pip',
    commission: 'Không',
    leverage: '1:Không giới hạn',
    regulation: 'FCA · CySEC · FSA',
    color: '#10b981',
  },
  {
    name: 'XM',
    initial: 'XM',
    platform: 'MetaTrader 4 / 5',
    spread: 'từ 0.6 pip',
    commission: 'Không',
    leverage: '1:1000',
    regulation: 'ASIC · CySEC · FSC',
    color: '#f59e0b',
  },
];

export type Trade = {
  date: string;
  dir: 'long' | 'short';
  symbol: string;
  lot: number;
  pnl: number;
  session: 'Asia' | 'London' | 'New York';
};

export const SAMPLE_TRADES: Trade[] = [
  { date: '2025-01-06', dir: 'long', symbol: 'EURUSD', lot: 0.5, pnl: 42.5, session: 'London' },
  { date: '2025-01-07', dir: 'short', symbol: 'GBPUSD', lot: 0.3, pnl: -18.2, session: 'London' },
  { date: '2025-01-08', dir: 'long', symbol: 'EURUSD', lot: 0.4, pnl: 61.0, session: 'New York' },
  { date: '2025-01-09', dir: 'short', symbol: 'USDJPY', lot: 0.5, pnl: -27.4, session: 'Asia' },
  { date: '2025-01-10', dir: 'long', symbol: 'AUDUSD', lot: 0.6, pnl: 38.8, session: 'Asia' },
  { date: '2025-01-13', dir: 'long', symbol: 'EURUSD', lot: 0.5, pnl: -22.1, session: 'London' },
  { date: '2025-01-14', dir: 'short', symbol: 'USDCAD', lot: 0.4, pnl: 55.6, session: 'New York' },
  { date: '2025-01-15', dir: 'long', symbol: 'GBPUSD', lot: 0.3, pnl: 29.3, session: 'London' },
  { date: '2025-01-16', dir: 'short', symbol: 'EURUSD', lot: 0.5, pnl: -35.0, session: 'New York' },
  { date: '2025-01-17', dir: 'long', symbol: 'USDCHF', lot: 0.4, pnl: 18.7, session: 'London' },
  { date: '2025-01-20', dir: 'short', symbol: 'GBPUSD', lot: 0.3, pnl: 44.2, session: 'London' },
  { date: '2025-01-21', dir: 'long', symbol: 'EURUSD', lot: 0.5, pnl: 71.5, session: 'New York' },
  { date: '2025-01-22', dir: 'short', symbol: 'USDJPY', lot: 0.4, pnl: -15.8, session: 'Asia' },
  { date: '2025-01-23', dir: 'long', symbol: 'NZDUSD', lot: 0.6, pnl: 25.4, session: 'Asia' },
  { date: '2025-01-24', dir: 'short', symbol: 'EURUSD', lot: 0.5, pnl: -48.9, session: 'London' },
  { date: '2025-01-27', dir: 'long', symbol: 'AUDUSD', lot: 0.4, pnl: 33.6, session: 'Asia' },
  { date: '2025-01-28', dir: 'short', symbol: 'USDCAD', lot: 0.5, pnl: 62.3, session: 'New York' },
  { date: '2025-01-29', dir: 'long', symbol: 'EURUSD', lot: 0.4, pnl: -19.5, session: 'London' },
  { date: '2025-01-30', dir: 'short', symbol: 'GBPUSD', lot: 0.3, pnl: 51.7, session: 'London' },
  { date: '2025-01-31', dir: 'long', symbol: 'USDCHF', lot: 0.4, pnl: 24.9, session: 'New York' },
  { date: '2025-02-03', dir: 'long', symbol: 'EURUSD', lot: 0.5, pnl: 47.1, session: 'London' },
  { date: '2025-02-04', dir: 'short', symbol: 'USDJPY', lot: 0.4, pnl: -31.2, session: 'Asia' },
  { date: '2025-02-05', dir: 'long', symbol: 'GBPUSD', lot: 0.3, pnl: 36.5, session: 'London' },
  { date: '2025-02-06', dir: 'short', symbol: 'EURUSD', lot: 0.5, pnl: -26.8, session: 'New York' },
  { date: '2025-02-07', dir: 'long', symbol: 'AUDUSD', lot: 0.5, pnl: 58.2, session: 'Asia' },
  { date: '2025-02-10', dir: 'short', symbol: 'USDCAD', lot: 0.4, pnl: -20.3, session: 'New York' },
  { date: '2025-02-11', dir: 'long', symbol: 'EURUSD', lot: 0.5, pnl: 66.4, session: 'London' },
  { date: '2025-02-12', dir: 'short', symbol: 'GBPUSD', lot: 0.3, pnl: 28.9, session: 'London' },
  { date: '2025-02-13', dir: 'long', symbol: 'NZDUSD', lot: 0.6, pnl: -14.7, session: 'Asia' },
  { date: '2025-02-14', dir: 'short', symbol: 'EURUSD', lot: 0.5, pnl: 39.1, session: 'New York' },
  { date: '2025-02-17', dir: 'long', symbol: 'USDCHF', lot: 0.4, pnl: 22.6, session: 'London' },
  { date: '2025-02-18', dir: 'short', symbol: 'USDJPY', lot: 0.5, pnl: -44.0, session: 'Asia' },
  { date: '2025-02-19', dir: 'long', symbol: 'EURUSD', lot: 0.5, pnl: 74.8, session: 'New York' },
  { date: '2025-02-20', dir: 'short', symbol: 'GBPUSD', lot: 0.3, pnl: 31.4, session: 'London' },
  { date: '2025-02-21', dir: 'long', symbol: 'AUDUSD', lot: 0.5, pnl: -23.9, session: 'Asia' },
  { date: '2025-02-24', dir: 'short', symbol: 'EURUSD', lot: 0.4, pnl: 49.5, session: 'London' },
  { date: '2025-02-25', dir: 'long', symbol: 'USDCAD', lot: 0.4, pnl: 17.2, session: 'New York' },
  { date: '2025-02-26', dir: 'short', symbol: 'USDJPY', lot: 0.5, pnl: -52.6, session: 'Asia' },
  { date: '2025-02-27', dir: 'long', symbol: 'EURUSD', lot: 0.5, pnl: 60.1, session: 'London' },
  { date: '2025-02-28', dir: 'short', symbol: 'GBPUSD', lot: 0.3, pnl: -16.4, session: 'London' },
  { date: '2025-03-03', dir: 'long', symbol: 'EURUSD', lot: 0.5, pnl: 43.7, session: 'New York' },
  { date: '2025-03-04', dir: 'short', symbol: 'AUDUSD', lot: 0.4, pnl: 27.8, session: 'Asia' },
  { date: '2025-03-05', dir: 'long', symbol: 'GBPUSD', lot: 0.3, pnl: -21.5, session: 'London' },
  { date: '2025-03-06', dir: 'short', symbol: 'EURUSD', lot: 0.5, pnl: 55.0, session: 'London' },
  { date: '2025-03-07', dir: 'long', symbol: 'USDCHF', lot: 0.4, pnl: 19.8, session: 'New York' },
  { date: '2025-03-10', dir: 'short', symbol: 'USDJPY', lot: 0.5, pnl: -38.3, session: 'Asia' },
  { date: '2025-03-11', dir: 'long', symbol: 'EURUSD', lot: 0.5, pnl: 68.9, session: 'London' },
  { date: '2025-03-12', dir: 'short', symbol: 'GBPUSD', lot: 0.3, pnl: 34.1, session: 'London' },
  { date: '2025-03-13', dir: 'long', symbol: 'AUDUSD', lot: 0.5, pnl: -25.7, session: 'Asia' },
  { date: '2025-03-14', dir: 'short', symbol: 'EURUSD', lot: 0.5, pnl: 46.2, session: 'New York' },
  { date: '2025-03-17', dir: 'long', symbol: 'USDJPY', lot: 0.4, pnl: 12.9, session: 'Asia' },
  { date: '2025-03-18', dir: 'short', symbol: 'GBPUSD', lot: 0.3, pnl: -28.6, session: 'London' },
  { date: '2025-03-19', dir: 'long', symbol: 'EURUSD', lot: 0.5, pnl: 57.4, session: 'New York' },
  { date: '2025-03-20', dir: 'short', symbol: 'USDCAD', lot: 0.4, pnl: -13.2, session: 'New York' },
  { date: '2025-03-21', dir: 'long', symbol: 'NZDUSD', lot: 0.5, pnl: 41.6, session: 'Asia' },
  { date: '2025-03-24', dir: 'short', symbol: 'EURUSD', lot: 0.5, pnl: 52.8, session: 'London' },
  { date: '2025-03-25', dir: 'long', symbol: 'GBPUSD', lot: 0.3, pnl: -17.9, session: 'London' },
  { date: '2025-03-26', dir: 'short', symbol: 'AUDUSD', lot: 0.4, pnl: 30.5, session: 'Asia' },
  { date: '2025-03-27', dir: 'long', symbol: 'EURUSD', lot: 0.5, pnl: 64.2, session: 'New York' },
  { date: '2025-03-28', dir: 'short', symbol: 'USDJPY', lot: 0.5, pnl: -33.7, session: 'Asia' },
];